import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useAttemptReview,
  useSaveReview,
  useScreeningRecording,
  type ScreeningChapter,
} from "@/api/tests-api";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth-store";

/**
 * Watching a video screening round, and writing notes against it.
 *
 * TWO THINGS SIDE BY SIDE, deliberately, and the notes never go away:
 * "the text area shall be active all the time on the left/right panel for
 * them to make live notes". An assessor typing while they watch must not
 * have to stop the video, open a panel, and find their place again.
 *
 * THE CHAPTERS ARE THE POINT OF THE LIST. A sitting is one unbroken file
 * of a quarter of an hour, so without an index the only way to reach
 * question seven is to watch the six before it. Each row seeks.
 *
 * SEEKING NEEDS HTTP RANGE, which is why the video is a signed /media
 * URL rather than a stream proxied through the API: the SPA's bearer
 * token is in memory and a bare <video src> cannot send it, so a proxy
 * could only be fed by downloading the whole file first. The URL is
 * short-lived and minting it stays behind MANAGE_TESTS.
 */

const AUTOSAVE_MS = 900;

function mmss(total: number | null | undefined): string {
  const s = Math.max(0, Math.round(total ?? 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export function ScreeningReview({
  attemptId,
  questions,
}: {
  attemptId: string;
  /** The sealed questions, so a chapter can show what was ASKED. */
  questions?: { id: string; prompt: string }[];
}) {
  const { toast } = useToast();
  const me = useAuthStore((st) => st.user);
  const recording = useScreeningRecording(attemptId);
  const existing = useAttemptReview(attemptId);
  const save = useSaveReview();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playhead, setPlayhead] = useState(0);
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const seeded = useRef(false);

  // MY note, not whoever saved last. Several people review one
  // candidate and each keeps their own row -- the whole reason
  // TestReview is keyed by reviewer -- so seeding from the first row
  // would put somebody else's words in this assessor's box and then
  // overwrite theirs with it on the next keystroke.
  const mine = useMemo(
    () =>
      (existing.data?.overall_reviews ?? []).find(
        (r) => r.reviewer_user_id === me?.id,
      ) ?? null,
    [existing.data, me?.id],
  );

  // Seed ONCE, when the stored note arrives. Re-seeding on every refetch
  // would overwrite whatever the assessor has typed since -- the failure
  // the candidate application form's own autosave had to avoid.
  useEffect(() => {
    if (seeded.current || existing.data === undefined) return;
    seeded.current = true;
    setNotes(mine?.comments ?? "");
  }, [existing.data, mine]);

  const persist = useCallback(
    async (text: string) => {
      try {
        await save.mutateAsync({
          attemptId,
          // null = the OVERALL row, which is this reviewer's note on the
          // whole sitting. A video round has no per-case answers to hang
          // one off.
          answerId: null,
          body: {
            comments: text,
            // Carried through rather than nulled: the round scores
            // nothing, but an assessor who set an outcome elsewhere must
            // not have it wiped by typing a note.
            score: mine?.score ?? null,
            max_score: mine?.max_score ?? null,
            outcome: mine?.outcome ?? null,
          },
        });
        setDirty(false);
        setSavedAt(new Date().toLocaleTimeString());
      } catch {
        // Left dirty on purpose: the indicator keeps saying "unsaved",
        // the next keystroke retries, and nothing the assessor typed is
        // thrown away or silently reported as kept.
        toast({
          title: "Notes not saved yet",
          description: "Still trying — keep typing.",
          variant: "error",
        });
      }
    },
    [attemptId, save, toast, mine],
  );

  // Debounced, because an assessor types in bursts while watching and a
  // request per keystroke would be both wasteful and slower to settle.
  useEffect(() => {
    if (!dirty) return undefined;
    const id = window.setTimeout(() => void persist(notes), AUTOSAVE_MS);
    return () => window.clearTimeout(id);
  }, [notes, dirty, persist]);

  // A half-typed note must survive closing the console.
  useEffect(() => {
    return () => {
      if (dirty) void persist(notes);
    };
  }, [dirty, notes, persist]);

  const promptById = useMemo(
    () => new Map((questions ?? []).map((q) => [q.id, q.prompt])),
    [questions],
  );

  const chapters = recording.data?.chapters ?? [];
  const url = recording.data?.url ?? null;

  const seek = (chapter: ScreeningChapter) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = chapter.start_offset;
    void el.play().catch(() => undefined);
  };

  const activeIndex = chapters.findIndex(
    (c) =>
      playhead >= c.start_offset &&
      (c.end_offset == null || playhead < c.end_offset),
  );

  if (recording.isLoading) {
    return <p className="text-sm text-foreground-muted">Loading the recording…</p>;
  }

  if (!url) {
    return (
      <p
        data-cy="no-screening-recording"
        className="rounded-lg border border-border bg-background-card p-4 text-sm text-foreground-muted"
      >
        No recording was assembled for this sitting. That is a real outcome
        rather than a fault: a candidate can open a round, never enable the
        camera, and leave.
      </p>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-3">
        <video
          ref={videoRef}
          src={url}
          controls
          data-cy="screening-video"
          onTimeUpdate={(e) => setPlayhead(e.currentTarget.currentTime)}
          className="w-full rounded-xl border border-border bg-black"
        />
        <div className="text-xs text-foreground-muted">
          {mmss(recording.data?.duration_seconds)} total ·{" "}
          {chapters.length} question{chapters.length === 1 ? "" : "s"}
        </div>

        <ol className="flex flex-col gap-1.5" data-cy="screening-chapters">
          {chapters.map((c, i) => {
            const over = c.ended_by === "timer";
            return (
              <li key={c.index}>
                <button
                  type="button"
                  data-cy={`btn-chapter-${c.index}`}
                  onClick={() => seek(c)}
                  className={[
                    "flex w-full items-start gap-3 rounded-lg border p-2.5 text-left",
                    i === activeIndex
                      ? "border-accent bg-accent/5"
                      : "border-border hover:border-accent",
                  ].join(" ")}
                >
                  <span className="mt-0.5 shrink-0 font-mono text-xs tabular-nums text-foreground-muted">
                    {mmss(c.start_offset)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-foreground">
                      {promptById.get(c.question_id) ?? `Question ${c.index + 1}`}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-foreground-muted">
                      {/* HOW it ended, which the recording alone does not
                          show. A candidate who used every second of every
                          question is saying something different from one
                          who moved on early. */}
                      {over ? "Ran out of time" : "Moved on"} ·{" "}
                      {mmss((c.end_offset ?? c.start_offset) - c.start_offset)} of{" "}
                      {mmss(c.allotted_seconds)}
                      {c.paused_seconds > 0 &&
                        ` · disconnected for ${mmss(c.paused_seconds)}`}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      {/* The notes, always here. Not a dialog, not a tab. */}
      <div className="flex flex-col">
        <label className="sk-field flex flex-1 flex-col">
          <span className="sk-label">Assessor notes</span>
          <textarea
            data-cy="screening-notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setDirty(true);
            }}
            placeholder="Type while you watch. Saved automatically."
            className="sk-input mt-1 min-h-[420px] flex-1 resize-y"
          />
        </label>
        <p className="mt-1.5 text-[11px] text-foreground-muted" data-cy="notes-state">
          {dirty
            ? "Unsaved…"
            : savedAt
              ? `Saved at ${savedAt}`
              : "Saved automatically as you type."}
        </p>
      </div>
    </div>
  );
}
